import { useState, type ReactElement } from 'react';

import type { StreamState } from '@harness/stream-core';
import type { AssistantTurn, UserTurn } from '../hooks/use-chat.ts';
import { AssistantBody } from './assistant-body.tsx';
import { FeedbackDialog } from './feedback-dialog.tsx';
import { GapNotice } from './gap-notice.tsx';
import { QueueNotice } from './queue-notice.tsx';
import { ReasoningPanel } from './reasoning-panel.tsx';
import { RedactionNotice } from './redaction-notice.tsx';
import { StalledNotice, StreamError } from './stream-error.tsx';

export interface MessageActions {
  onRetry: () => void;
  onCancel: () => void;
  onRestart: () => void;
}

export function UserMessage({ turn }: { turn: UserTurn }): ReactElement {
  return (
    <article className="message message--user">
      <div className="message__body">{turn.text}</div>
    </article>
  );
}

const WAITING_PHASES = new Set(['idle', 'connecting', 'thinking', 'reconnecting']);

function computeMessageMetrics(state: StreamState): {
  ttftMs: number | null;
  durationSec: number | null;
} {
  if (state.timeline.length === 0) return { ttftMs: null, durationSec: null };
  const firstEntry = state.timeline[0];
  const firstToken = state.timeline.find(
    (entry) => entry.kind === 'frame' && entry.type === 'response.output_text.delta',
  );
  const lastEntry = state.timeline[state.timeline.length - 1];

  /* performance.now is sub millisecond, so the raw difference prints as
     1199.4000000003725ms. The clock's precision is real; that many digits of
     it are not, and a footer that shows them reads as unfinished. */
  const ttftMs =
    firstToken !== undefined && firstEntry !== undefined
      ? Math.round(Math.max(0, firstToken.at - firstEntry.at))
      : null;
  const durationSec =
    firstEntry !== undefined && lastEntry !== undefined && lastEntry.at > firstEntry.at
      ? (lastEntry.at - firstEntry.at) / 1000
      : null;

  return { ttftMs, durationSec };
}

export function AssistantMessage({
  turn,
  live,
  actions,
}: {
  turn: AssistantTurn;
  /** True for the newest assistant turn, whose machine still exists. */
  live: boolean;
  actions: MessageActions;
}): ReactElement {
  const { state } = turn;
  const [reportOpen, setReportOpen] = useState(false);
  const metrics = computeMessageMetrics(state);

  const streaming = state.phase === 'streaming';
  const terminal = state.phase === 'done' || state.phase === 'error' || state.phase === 'cancelled';
  const waiting = WAITING_PHASES.has(state.phase) && state.text === '';
  const hasReasoning = state.reasoning !== null || state.toolCalls.length > 0;

  return (
    <article className="message message--assistant">
      {hasReasoning && (
        <ReasoningPanel
          reasoning={state.reasoning ?? {}}
          toolCalls={state.toolCalls}
          generationPhase={state.generationPhase}
        />
      )}

      {state.phase === 'queued' && state.queue !== null && (
        <QueueNotice queue={state.queue} live={live} onCancel={actions.onCancel} />
      )}

      {state.error !== null && (
        <StreamError
          error={state.error}
          categories={state.redaction?.categories ?? []}
          live={live}
          onRetry={actions.onRetry}
          onRestart={actions.onRestart}
        />
      )}

      {state.phase === 'cancelled' && state.error === null && (
        <StreamError
          error={{ code: 'stream_aborted' }}
          categories={[]}
          live={live}
          onRetry={actions.onRetry}
          onRestart={actions.onRestart}
        />
      )}

      {state.phase === 'stalled' && <StalledNotice live={live} onRetry={actions.onRetry} />}

      <div className="message__body">
        {waiting ? (
          <div className="message__waiting" role="status" aria-live="polite">
            <p className="muted message__waiting-label">
              {state.phase === 'thinking' ? 'Thinking.' : 'Connecting.'}
            </p>
            <div className="skeleton" aria-hidden="true">
              <span className="skeleton__line" />
              <span className="skeleton__line" />
              <span className="skeleton__line" />
            </div>
          </div>
        ) : (
          state.text !== '' && <AssistantBody text={state.text} streaming={streaming} />
        )}
      </div>

      {state.redaction !== null && <RedactionNotice redaction={state.redaction} />}
      <GapNotice state={state} />

      {state.truncated !== null && (
        <p className="message__truncated muted">
          Cut short: <span className="mono">{state.truncated}</span>. The text above is what
          arrived.
        </p>
      )}

      <footer className="message__footer">
        <span className="mono">{state.phase}</span>
        {state.usage !== null && (
          <span className="mono muted">{state.usage.output_tokens} tokens</span>
        )}
        {metrics.ttftMs !== null && <span className="mono muted">TTFT: {metrics.ttftMs}ms</span>}
        {metrics.durationSec !== null && terminal && (
          <span className="mono muted">{metrics.durationSec.toFixed(1)}s</span>
        )}
        {state.reconnects > 0 && (
          <span className="mono muted">
            {state.reconnects} reconnect{state.reconnects === 1 ? '' : 's'}
          </span>
        )}
        {terminal && (
          <button
            type="button"
            className="button button--quiet message__report"
            onClick={() => {
              setReportOpen(true);
            }}
          >
            Report
          </button>
        )}
      </footer>

      <FeedbackDialog
        streamId={state.streamId}
        open={reportOpen}
        onClose={() => {
          setReportOpen(false);
        }}
      />
    </article>
  );
}
